import { CalendarIcon, DialogueBubbleIcon, TimeIcon } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { EIsoTimeUnit, parseIsoDuration, parseCombinedIsoDuration } from 'src/utils/duration-iso.util'
import { getDateValuesFromSchedule } from '../messages/_components/programmed-message.helper'
import { TTimelineChatbotElement } from './followup-timeline.type'

const TimelineChatbotItemTooltip = ({
  timelineElement,
  correspondingMedEventName,
}: {
  timelineElement: TTimelineChatbotElement
  correspondingMedEventName: string
}) => {
  const { t } = useTranslation()
  const options = timelineElement.options

  const formatDuration = (days: number, hours: number, minutes: number) =>
    [
      days > 0 ? t('pages.configuration.messages.duration-days', { count: days }) : null,
      hours > 0 ? t('pages.configuration.messages.duration-hours', { count: hours }) : null,
      minutes > 0 ? t('pages.configuration.messages.duration-minutes', { count: minutes }) : null,
    ]
      .filter(Boolean)
      .join(' ')

  let autoEndText: string | null = null
  if (options?.automaticEnd?.expireDuration) {
    try {
      const { value, unit } = parseIsoDuration(options.automaticEnd.expireDuration)
      const durationByUnit: Record<EIsoTimeUnit, string> = {
        [EIsoTimeUnit.HOURS]: t('pages.configuration.messages.duration-hours', { count: value }),
        [EIsoTimeUnit.MINUTES]: t('pages.configuration.messages.duration-minutes', { count: value }),
        [EIsoTimeUnit.DAYS]: t('pages.configuration.messages.duration-days', { count: value }),
        [EIsoTimeUnit.WEEKS]: t('pages.configuration.messages.duration-days', { count: value * 7 }),
        [EIsoTimeUnit.MONTHS]: t('pages.configuration.messages.duration-days', { count: value * 30 }),
      }
      autoEndText = t('pages.configuration.followups.chatbot.tooltip.auto-end', {
        duration: durationByUnit[unit],
      })
    } catch {
      /* invalid duration — skip */
    }
  }

  let autoRelaunchText: string | null = null
  if (options?.automaticRelaunchDelay) {
    try {
      const { days, hours, minutes } = parseCombinedIsoDuration(options.automaticRelaunchDelay)
      autoRelaunchText = t('pages.configuration.followups.chatbot.tooltip.auto-relaunch', {
        duration: formatDuration(days, hours, minutes),
      })
    } catch {
      /* invalid duration — skip */
    }
  }

  return (
    <span className="c-absolute c-shadow-xl c-left-[calc(100%+10px)] c-w-80 c-top-0 c-flex-col c-z-10 c-border c-border-grey-500 c-bg-white c-hidden group-hover:c-flex c-p-3 c-rounded-md c-text-black c-text-left">
      <h3 className="c-text-sm c-font-bold c-pb-1">
        {timelineElement.name} (
        {timelineElement.isActive
          ? t('pages.configuration.followups.chatbot.published')
          : t('pages.configuration.followups.chatbot.unpublished')}
        )
      </h3>
      <hr className="c-mb-3 c-border-t-grey-300" />
      <ul>
        <li className="c-flex c-mb-4 c-font-bold c-items-center">
          <CalendarIcon className="c-w-5 c-mr-2" />
          {t('pages.configuration.followups.message-tooltip-date', {
            ...getDateValuesFromSchedule(timelineElement.schedule),
            medEventName: correspondingMedEventName || '',
          })}
        </li>
        {timelineElement.schedule.atTime && (
          <li className="c-flex c-mb-4 c-font-bold c-items-center">
            <TimeIcon className="c-w-5 c-mr-2" />
            {t('pages.configuration.followups.at-time', { time: timelineElement.schedule.atTime })}
          </li>
        )}
        {timelineElement.nodeCount !== undefined && (
          <li className="c-flex c-mb-4 c-font-bold c-items-center">
            <DialogueBubbleIcon className="c-w-5 c-mr-2" />
            {t('pages.configuration.followups.chatbot.node-count', { count: timelineElement.nodeCount })}
          </li>
        )}
        {autoEndText && (
          <li className="c-flex c-mb-2 c-items-start c-text-xs">
            <span className="c-mr-2 c-text-grey-400 c-shrink-0">•</span>
            {autoEndText}
          </li>
        )}
        {timelineElement.schedule.avoidHolidays && (
          <li className="c-flex c-mb-2 c-items-start c-text-xs">
            <span className="c-mr-2 c-text-grey-400 c-shrink-0">•</span>
            {t('pages.configuration.followups.chatbot.tooltip.avoid-holidays')}
          </li>
        )}
        {autoRelaunchText && (
          <li className="c-flex c-mb-2 c-items-start c-text-xs">
            <span className="c-mr-2 c-text-grey-400 c-shrink-0">•</span>
            {autoRelaunchText}
          </li>
        )}
        {options?.showUnreachedPatientInDashboardDelay && (
          <li className="c-flex c-mb-2 c-items-start c-text-xs">
            <span className="c-mr-2 c-text-grey-400 c-shrink-0">•</span>
            {t('pages.configuration.followups.chatbot.tooltip.show-unreached')}
          </li>
        )}
      </ul>
    </span>
  )
}

export { TimelineChatbotItemTooltip }
